import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  AccessibilityNew as AccessibilityNewIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
  Bookmark as BookmarkIcon,
  AccessTimeFilled as AccessTimeFilledIcon,
  AccessAlarm as AccessAlarmIcon,
  TaskAlt as TaskAltIcon,
  PhonelinkErase as PhonelinkEraseIcon,
  ThumbUp as ThumbUpIcon,
  Cancel as CancelIcon,
  WorkHistory as WorkHistoryIcon,
  Backspace as BackspaceIcon,
  ShowChart as ShowChartIcon,
  BarChart as BarChartIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, PointElement, LineElement, Title, Filler,
} from 'chart.js';

import { get_sales_dashboard_advanced_CRM_DATA } from '../../Redux/Actions/RoleData_Actions';
import { GetAll_stage_Data }                      from '../../Redux/Actions/Stage_Action';
import { useSidebarWidth }                         from '../../Utility/useSidebarWidth';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

/* ─── Design tokens — same as SalesDashboard ─────────────────────────────── */
const C = {
  bg:         '#F7F8FC',
  white:      '#FFFFFF',
  border:     '#E8EAF0',
  accent:     '#0D47A1',
  accentSoft: 'rgba(13,71,161,0.07)',
  text:       '#1C2340',
  textMuted:  '#7B829A',
  success:    '#12B76A',
  warning:    '#F79009',
  danger:     '#F04438',
  shadow:     '0 1px 3px rgba(16,24,40,0.08)',
  shadowMd:   '0 4px 16px rgba(16,24,40,0.10)',
};

const SIDEBAR_COLLAPSED = 64;

/* ─── Stage icons ─────────────────────────────────────────────────────────── */
const STAGE_ICONS = {
  Fresh:                    <TipsAndUpdatesIcon />,
  'No Stage':               <TipsAndUpdatesIcon />,
  'Follow Up':              <BookmarkIcon />,
  Follow:                   <BookmarkIcon />,
  'Long Follow':            <BookmarkIcon />,
  Transfer:                 <BackspaceIcon />,
  Interested:               <ThumbUpIcon />,
  'Not Interested':         <BackspaceIcon />,
  Meeting:                  <AccessTimeFilledIcon />,
  'Following After Meeting':<WorkHistoryIcon />,
  'Follow After Meeting':   <WorkHistoryIcon />,
  'Cancel Meeting':         <CancelIcon />,
  'Done Deal':              <TaskAltIcon />,
  'No Answer':              <PhonelinkEraseIcon />,
  'No Answer 1st Call':     <AccessAlarmIcon />,
  Cancellation:             <CancelIcon />,
  Pending:                  <AccessibilityNewIcon />,
};

const stageColor = (name) => name === 'Pending' ? '#F79009' : C.accent;

/* ─── Stat card — identical to SalesDashboard ────────────────────────────── */
const StatCard = ({ label, value, sub, highlight, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: highlight ? C.accent : C.white,
      border: `1px solid ${highlight ? C.accent : C.border}`,
      borderRadius: 12,
      padding: '20px 24px',
      minWidth: 160,
      height: 120,
      boxShadow: highlight ? '0 4px 16px rgba(13,71,161,0.25)' : C.shadow,
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      cursor: onClick ? 'pointer' : 'default',
      flexShrink: 0,
      transition: 'transform 0.15s, box-shadow 0.15s',
    }}
    onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(13,71,161,0.18)'; } }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = highlight ? '0 4px 16px rgba(13,71,161,0.25)' : C.shadow; }}
  >
    <span style={{ fontSize: 11, fontWeight: 500, color: highlight ? 'rgba(255,255,255,0.75)' : C.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif" }}>{label}</span>
    <span style={{ fontSize: 26, fontWeight: 700, color: highlight ? '#fff' : C.text, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 }}>{value ?? '—'}</span>
    {sub && <span style={{ fontSize: 11, color: highlight ? 'rgba(255,255,255,0.6)' : C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{sub}</span>}
    {highlight && <div style={{ height: 3, width: 40, background: C.warning, borderRadius: 2 }} />}
  </div>
);

/* ─── Stage card — identical to SalesDashboard's StageCard ───────────────── */
const StageCard = ({ stage, displayName, leadsCount, totalLeads, onClick }) => {
  const color      = stageColor(stage.name);
  const pct        = totalLeads > 0 ? ((leadsCount / totalLeads) * 100).toFixed(1) : 0;
  const icon       = STAGE_ICONS[stage.name] || <AccessibilityNewIcon />;
  const isDoneDeal = stage.name === 'Done Deal';
  const isPending  = stage.name === 'Pending';

  return (
    <div
      onClick={onClick}
      style={{
        background:  isDoneDeal ? '#F79009' : isPending ? '#FFF8EC' : C.white,
        border: `1px solid ${isDoneDeal ? '#F79009' : isPending ? '#F7900940' : C.border}`,
        borderRadius: 10,
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
        boxShadow: isDoneDeal ? '0 4px 16px rgba(247,144,9,0.30)' : C.shadow,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, minHeight: 70,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = isDoneDeal ? '0 8px 24px rgba(247,144,9,0.40)' : C.shadowMd;
        if (!isDoneDeal && !isPending) e.currentTarget.style.borderColor = color;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = isDoneDeal ? '0 4px 16px rgba(247,144,9,0.30)' : C.shadow;
        e.currentTarget.style.borderColor = isDoneDeal ? '#F79009' : isPending ? '#F7900940' : C.border;
      }}
    >
      {/* Left: name + count */}
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase',
          color: isDoneDeal ? 'rgba(255,255,255,0.85)' : C.textMuted,
          fontFamily: "'DM Sans', sans-serif", marginBottom: 4,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {displayName}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <span style={{
            fontSize: 24, fontWeight: 800, lineHeight: 1,
            color: isDoneDeal ? '#fff' : isPending ? '#F79009' : color,
            fontFamily: "'DM Sans', sans-serif",
          }}>{leadsCount}</span>
          <span style={{
            fontSize: 11, fontWeight: 500,
            color: isDoneDeal ? 'rgba(255,255,255,0.7)' : C.textMuted,
            fontFamily: "'DM Sans', sans-serif",
          }}>{pct}%</span>
        </div>
      </div>
      {/* Right: icon */}
      <div style={{ flexShrink: 0, opacity: isDoneDeal ? 0.9 : 0.55, display: 'flex', alignItems: 'center' }}>
        {React.cloneElement(icon, { sx: { color: isDoneDeal ? '#fff' : color, fontSize: 22 } })}
      </div>
    </div>
  );
};

/* ─── Stage list row — identical to SalesDashboard's StageRow ────────────── */
const StageRow = ({ stage, displayName, leadsCount, totalLeads, onClick }) => {
  const color = stageColor(stage.name);
  const pct   = totalLeads > 0 ? ((leadsCount / totalLeads) * 100).toFixed(1) : 0;
  const icon  = STAGE_ICONS[stage.name] || <AccessibilityNewIcon />;

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px',
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        cursor: 'pointer',
        marginBottom: 8,
        transition: 'transform 0.15s, border-color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(4px)'; e.currentTarget.style.borderColor = color; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.borderColor = C.border; }}
    >
      <div style={{ width: 34, height: 34, borderRadius: 8, background: color + '12', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 18 } })}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: "'DM Sans', sans-serif", marginBottom: 5 }}>{displayName}</div>
        <div style={{ height: 4, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color, fontFamily: "'DM Sans', sans-serif" }}>{leadsCount}</div>
        <div style={{ fontSize: 11, color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{pct}%</div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════════════════════ */
export const SalesQuickNumberCRMData = () => {
  const theme    = useTheme();
  const mode     = theme.palette.mode;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sidebarW = useSidebarWidth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [userlogin, setUserlogin] = useState(null);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  useEffect(() => { dispatch(GetAll_stage_Data()); }, [dispatch]);

  const resAllStages  = useSelector(s => s.Allstages.stage);
  const dashboardData = useSelector(s => s.AllRoleData?.salesdashboard);

  const stagearray     = Array.isArray(resAllStages?.data) ? resAllStages.data : [];
  const filteredStages = stagearray.filter(s => s.name !== 'Fresh');

  const STAGE_ORDER = ['Pending','No Stage','Fresh','Follow Up','Meeting','Follow After Meeting','Following After Meeting','Interested','Not Interested','No Answer','No Answer 1st Call','Done Deal','Cancellation','Cancel Meeting','Transfer','Long Follow','Follow'];

  const sortedStages = useMemo(() =>
    [...filteredStages].sort((a, b) => {
      const ia = STAGE_ORDER.indexOf(a.name), ib = STAGE_ORDER.indexOf(b.name);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1; if (ib !== -1) return 1; return 0;
    }), [filteredStages]);

  useEffect(() => {
    if (userlogin?.data?.email) {
      setLoading(true);
      dispatch(get_sales_dashboard_advanced_CRM_DATA(userlogin.data.email))
        .finally(() => setLoading(false));
    }
  }, [userlogin, dispatch]);

  /* ── computed ── */
  const totalLeads     = dashboardData?.data?.summary?.totalLeads || 0;
  const doneDealsCount = useMemo(() => dashboardData?.data?.stages?.find(s => s.stageName === 'Done Deal')?.leadsCount || 0, [dashboardData]);
  const convRate       = totalLeads > 0 ? ((doneDealsCount / totalLeads) * 100).toFixed(1) : 0;

  const getCount       = (name) => dashboardData?.data?.stages?.find(s => s.stageName === name)?.leadsCount || 0;
  const getDisplayName = (name) => name === 'No Stage' ? 'Fresh' : name;

  const handleStageClick = (displayName) => {
    const api = displayName === 'Fresh' ? 'No Stage' : displayName;
    localStorage.setItem('stagestorage', api);
    navigate(`/SalesAllLeadsCRMDATA?stage=${encodeURIComponent(api)}&data=true&transferefromdata=true`);
  };

  /* ── chart ── */
  const chartData = useMemo(() => {
    const labels = [], data = [], colors = [];
    sortedStages.forEach(s => {
      if (s.name === 'Fresh') return;
      const count = getCount(s.name);
      if (count > 0 || s.name === 'No Stage') {
        labels.push(getDisplayName(s.name));
        data.push(count);
        colors.push(stageColor(s.name));
      }
    });
    return {
      labels,
      datasets: [{ data, backgroundColor: colors, borderColor: mode === 'dark' ? '#1a1a2e' : '#fff', borderWidth: 2, hoverOffset: 12 }],
    };
  }, [dashboardData, sortedStages, mode]);

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: { color: mode === 'dark' ? '#fff' : C.text, font: { size: 11, family: "'DM Sans', sans-serif" }, padding: 16, usePointStyle: true, pointStyle: 'circle' },
      },
      tooltip: {
        backgroundColor: C.white, titleColor: C.text, bodyColor: C.textMuted,
        borderColor: C.border, borderWidth: 1,
        callbacks: { label: ctx => { const t = ctx.dataset.data.reduce((a,b)=>a+b,0); return `${ctx.label}: ${ctx.raw} leads (${t>0?((ctx.raw/t)*100).toFixed(1):0}%)`; } },
      },
    },
    cutout: '62%',
  };

  /* ── loading ── */
  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background: C.bg }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:48, height:48, borderRadius:'50%', border:`3px solid ${C.accentSoft}`, borderTop:`3px solid ${C.accent}`, animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }} />
        <div style={{ fontSize:14, color:C.textMuted, fontFamily:"'DM Sans', sans-serif" }}>Loading CRM Data...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  /* ── render ── */
  return (
    <div style={{
      marginLeft: isMobile ? SIDEBAR_COLLAPSED : sidebarW,
      marginTop: 64,
      minHeight: 'calc(100vh - 64px)',
      background: C.bg,
      transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ padding: isMobile ? '16px' : '28px 28px 48px' }}>

        {/* ── Header ── */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:16, marginBottom:28 }}>
          <div>
            <div style={{ fontSize: isMobile ? 28 : 36, fontWeight:800, color:C.text, lineHeight:1.15, letterSpacing:'-0.02em', fontFamily:"'DM Sans', sans-serif" }}>
              CRM Data<br />Dashboard
            </div>
            <div style={{ fontSize:13.5, color:C.textMuted, marginTop:8, fontFamily:"'DM Sans', sans-serif" }}>
              Welcome back, <strong style={{ color:C.text }}>{userlogin?.data?.name || 'Sales'}</strong> — Data Center leads and performance insights.
            </div>
          </div>

          {/* Stat cards */}
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'stretch' }}>
            <StatCard
              label="Total Leads"
              value={totalLeads}
              onClick={() => navigate('/SalesAllLeadsCRMDATA')}
            />
            <StatCard
              label="Done Deals"
              value={doneDealsCount}
              onClick={() => navigate('/SalesAllLeadsCRMDATA?stage=Done+Deal&data=true&transferefromdata=true')}
            />
            <StatCard
              label="Fresh Leads"
              value={getCount('No Stage')}
            />
            <StatCard
              label="Conv. Rate"
              value={`${convRate}%`}
              highlight
            />
          </div>
        </div>

        {/* ── Toolbar row ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <ShowChartIcon sx={{ color:C.accent, fontSize:18 }} />
            <span style={{ fontSize:16, fontWeight:700, color:C.text, fontFamily:"'DM Sans', sans-serif" }}>Data Center Stages</span>
            <span style={{ fontSize:12, color:C.textMuted, fontFamily:"'DM Sans', sans-serif" }}>— click any card to view its leads</span>
          </div>
          <Link to="/SalesDashboard" style={{ textDecoration:'none' }}>
            <button
              style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, border:`1px solid ${C.border}`, background:C.white, color:C.text, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans', sans-serif", transition:'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.text; }}
            >
              <DashboardIcon sx={{ fontSize:16 }} />
              Sales Dashboard
            </button>
          </Link>
        </div>

        {/* ── Stage cards grid ── */}
        <div style={{
          display:'grid',
          gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))',
          gap:12,
          marginBottom:28,
        }}>
          {sortedStages.map(stage => {
            if (stage.name === 'Fresh') return null;
            const count       = getCount(stage.name);
            const displayName = getDisplayName(stage.name);
            return (
              <StageCard
                key={stage._id}
                stage={stage}
                displayName={displayName}
                leadsCount={count}
                totalLeads={totalLeads}
                onClick={() => handleStageClick(displayName)}
              />
            );
          })}
        </div>

        {/* ── Chart + List row ── */}
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:16, marginBottom:28 }}>

          {/* Doughnut */}
          <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:C.shadow, overflow:'hidden' }}>
            <div style={{ padding:'20px 22px 16px', borderBottom:`1px solid ${C.border}` }}>
              <div style={{ fontSize:15, fontWeight:700, color:C.text, fontFamily:"'DM Sans', sans-serif" }}>
                <BarChartIcon sx={{ fontSize:17, color:C.accent, verticalAlign:'middle', mr:0.5 }} />
                Leads Distribution
              </div>
              <div style={{ fontSize:12, color:C.textMuted, fontFamily:"'DM Sans', sans-serif", marginTop:3 }}>Visual breakdown across all Data Center stages</div>
            </div>
            <div style={{ padding:'20px 22px', height:320 }}>
              <Doughnut data={chartData} options={chartOptions} />
            </div>
          </div>

          {/* Stages list */}
          <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:C.shadow, overflow:'hidden' }}>
            <div style={{ padding:'20px 22px 16px', borderBottom:`1px solid ${C.border}` }}>
              <div style={{ fontSize:15, fontWeight:700, color:C.text, fontFamily:"'DM Sans', sans-serif" }}>
                <BookmarkIcon sx={{ fontSize:17, color:C.accent, verticalAlign:'middle', mr:0.5 }} />
                Stages Overview
              </div>
              <div style={{ fontSize:12, color:C.textMuted, fontFamily:"'DM Sans', sans-serif", marginTop:3 }}>Click any stage to view Data Center leads</div>
            </div>
            <div style={{ padding:'16px 22px', maxHeight:320, overflowY:'auto' }}>
              {sortedStages.map(stage => {
                if (stage.name === 'Fresh') return null;
                return (
                  <StageRow
                    key={stage._id}
                    stage={stage}
                    displayName={getDisplayName(stage.name)}
                    leadsCount={getCount(stage.name)}
                    totalLeads={totalLeads}
                    onClick={() => handleStageClick(getDisplayName(stage.name))}
                  />
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};